// MemberBadges.tsx - Section badges avec responsive design
import React from 'react';
import BadgeProgression from '../BadgeProgression';
import { Trophy, Target, Award, Star } from 'lucide-react';

interface MemberBadgesProps {
    memberData: any;
}

const MemberBadges: React.FC<MemberBadgesProps> = ({ memberData }) => {
    const referralsCount = Number(memberData.referrals_count) || 0;
    const badgesCount = memberData.badges?.length || 0;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-orange-100 rounded-lg">
                        <Trophy className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Mes badges</h1>
                        <p className="text-gray-600">Vos récompenses et progressions</p>
                    </div>
                </div>

                {/* Résumé des badges */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-orange-100 rounded-lg">
                                <Trophy className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-orange-600">Badges obtenus</p>
                                <p className="text-2xl font-bold text-orange-800">{badgesCount}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border border-green-200">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <Target className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-green-600">Parrainages</p>
                                <p className="text-2xl font-bold text-green-800">{referralsCount}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <Star className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-purple-600">Rang actuel</p>
                                <p className="text-lg font-bold text-purple-800">
                                    {badgesCount === 0 ? 'Membre' : 
                                     badgesCount === 1 ? 'Actif' :
                                     badgesCount === 2 ? 'Confirmé' :
                                     badgesCount >= 3 ? 'Expert' : 'Membre'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Message de motivation */}
            {referralsCount === 0 && (
                <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-6 border border-blue-200">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <Award className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-blue-900">
                                Commencez votre parcours de badges ! 🚀
                            </h3>
                            <p className="text-blue-700 mt-1">
                                Parrainez votre premier membre pour débloquer le badge "Recruteur"
                            </p>
                        </div>
                    </div>
                    <div className="mt-4">
                        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium">
                            Voir comment parrainer
                        </button>
                    </div>
                </div>
            )}

            {/* Progression des badges */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-6">Progression et objectifs</h2>
                    <BadgeProgression memberData={memberData} />
                </div>
            </div>

            {/* Conseils et astuces */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-yellow-800 mb-4">
                    💡 Conseils pour obtenir plus de badges
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                        <div className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></div>
                            <p className="text-sm text-yellow-800">
                                <strong>Partagez votre code :</strong> Utilisez votre code de parrainage sur les réseaux sociaux
                            </p>
                        </div>
                        <div className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></div>
                            <p className="text-sm text-yellow-800">
                                <strong>Événements :</strong> Invitez vos contacts lors des événements UDB
                            </p>
                        </div>
                    </div>
                    <div className="space-y-3">
                        <div className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></div>
                            <p className="text-sm text-yellow-800">
                                <strong>WhatsApp :</strong> Partagez directement le lien d'adhésion
                            </p>
                        </div>
                        <div className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></div>
                            <p className="text-sm text-yellow-800">
                                <strong>Persistence :</strong> Chaque parrainage compte pour votre progression
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Récompenses spéciales */}
            {badgesCount >= 2 && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border border-purple-200">
                    <div className="flex items-center space-x-3 mb-4">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <Star className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-purple-900">
                                Félicitations ! 🎉
                            </h3>
                            <p className="text-purple-700">
                                Vous êtes un membre actif et engagé de l'UDB
                            </p>
                        </div>
                    </div>
                    <div className="bg-white/60 rounded-lg p-4">
                        <h4 className="font-semibold text-purple-900 mb-2">Avantages débloqués :</h4>
                        <ul className="text-sm text-purple-800 space-y-1">
                            <li>• Reconnaissance dans la communauté</li>
                            <li>• Mention spéciale lors des événements</li>
                            <li>• Priorité pour les formations et séminaires</li>
                            <li>• Opportunités de leadership dans l'organisation</li>
                        </ul>
                    </div>
                </div>
            )}

            {/* Statistiques détaillées */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistiques détaillées</h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">{referralsCount}</div>
                        <div className="text-sm text-gray-600">Total parrainages</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">{badgesCount}</div>
                        <div className="text-sm text-gray-600">Badges obtenus</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">
                            {Math.max(0, 5 - referralsCount)}
                        </div>
                        <div className="text-sm text-gray-600">Vers prochain badge</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">
                            {referralsCount > 0 ? Math.round((referralsCount / 5) * 100) : 0}%
                        </div>
                        <div className="text-sm text-gray-600">Progression</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MemberBadges;